import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';

// Default variables available for email templates
const DEFAULT_AVAILABLE_VARIABLES = [
  'contact_name',
  'organization_name',
  'phone_number',
  'ticket_id',
  'submission_uuid',
  'ticket_ref',
  'status_check_url',
  'campaign_sid',
  'submission_type'
];

// Status triggers - the template_key determines when an email is sent
// This maps template keys to human-readable status labels
const STATUS_TRIGGERS = [
  { value: 'in_review', label: 'Submission Under Review', description: 'Sent when a submission is being reviewed' },
  { value: 'submitted_to_twilio', label: 'Submitted to Twilio', description: 'Sent when submission is sent to Twilio' },
  { value: 'approved', label: 'Verification Approved', description: 'Sent when Twilio approves the verification' },
  { value: 'rejected', label: 'Verification Rejected', description: 'Sent when Twilio rejects the verification' },
  { value: 'resubmitted', label: 'Resubmitted for Review', description: 'Sent when a rejected submission is resubmitted' },
  { value: 'awaiting_twilio', label: 'Awaiting Twilio Review', description: 'Sent when waiting for Twilio decision' },
  { value: 'pending_review', label: 'Pending Initial Review', description: 'Sent when submission is first received' }
];

// Helper to get status label from template_key
const getStatusLabel = (templateKey) => {
  const status = STATUS_TRIGGERS.find(s => s.value === templateKey);
  return status ? status.label : templateKey;
};

export default function EmailTemplates() {
  const { user } = useAuth();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    template_key: '',
    name: '',
    description: '',
    subject: '',
    html_body: '',
    submission_type: '',
    is_active: true,
    available_variables: DEFAULT_AVAILABLE_VARIABLES
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email-templates');
      setTemplates(response.data.templates);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate({ ...template });
    setPreviewHtml(null);
  };

  const handleCancel = () => {
    setEditingTemplate(null);
    setPreviewHtml(null);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewTemplate({
      template_key: '',
      name: '',
      description: '',
      subject: '',
      html_body: '',
      submission_type: '',
      is_active: true,
      available_variables: DEFAULT_AVAILABLE_VARIABLES
    });
  };

  const handleCreateTemplate = async () => {
    if (!isAdmin) {
      toast.warning('Only admins can create templates');
      return;
    }

    // Validation
    if (!newTemplate.template_key || !newTemplate.name || !newTemplate.subject || !newTemplate.html_body) {
      toast.warning('Please fill in all required fields (Key, Name, Subject, Body)');
      return;
    }

    try {
      setSaving(true);
      await api.post('/email-templates', {
        template_key: newTemplate.template_key,
        name: newTemplate.name,
        description: newTemplate.description,
        subject: newTemplate.subject,
        html_body: newTemplate.html_body,
        available_variables: newTemplate.available_variables,
        is_active: newTemplate.is_active,
        submission_type: newTemplate.submission_type || null
      });

      await loadTemplates();
      handleCancelCreate();
      toast.success('Template created successfully!');
    } catch (err) {
      toast.error('Failed to create template: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const response = await api.post(
        `/email-templates/${editingTemplate.id}/preview`,
        {}
      );
      setPreviewHtml(response.data.preview);
    } catch (err) {
      toast.error('Failed to preview template: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSave = async () => {
    if (!isAdmin) {
      toast.warning('Only admins can edit templates');
      return;
    }

    try {
      setSaving(true);
      await api.put(
        `/email-templates/${editingTemplate.id}`,
        {
          name: editingTemplate.name,
          description: editingTemplate.description,
          subject: editingTemplate.subject,
          html_body: editingTemplate.html_body,
          available_variables: editingTemplate.available_variables,
          is_active: editingTemplate.is_active,
          submission_type: editingTemplate.submission_type || null
        }
      );

      await loadTemplates();
      setEditingTemplate(null);
      setPreviewHtml(null);
      toast.success('Template updated successfully!');
    } catch (err) {
      toast.error('Failed to save template: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (variable) => {
    if (!editingTemplate) return;

    const textarea = document.getElementById('html-body-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editingTemplate.html_body;
    const before = text.substring(0, start);
    const after = text.substring(end);

    const newText = before + `{{${variable}}}` + after;
    setEditingTemplate({ ...editingTemplate, html_body: newText });

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading templates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Email Templates</h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                Manage email templates sent to customers during the verification process
              </p>
            </div>
            <div className="flex gap-2">
              {isAdmin && !isCreating && !editingTemplate && (
                <button
                  onClick={() => setIsCreating(true)}
                  className="btn btn-primary whitespace-nowrap"
                >
                  + Create New Template
                </button>
              )}
              <Link
                to="/dashboard"
                className="btn btn-secondary whitespace-nowrap"
              >
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!isAdmin && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-4">
            <p className="font-medium">⚠️ View-Only Mode</p>
            <p className="text-sm">Only administrators can edit email templates.</p>
          </div>
        )}

        {/* Create Template Form */}
        {isCreating && (
          <div className="space-y-6 mb-8">
            <div className="card">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Create New Template</h2>
                <button
                  onClick={handleCancelCreate}
                  className="btn btn-secondary whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>

              <div className="space-y-4">
                {/* Status Trigger (Template Key) */}
                <div>
                  <label className="label">Triggered By Status *</label>
                  <select
                    className="input"
                    value={newTemplate.template_key}
                    onChange={(e) => setNewTemplate({ ...newTemplate, template_key: e.target.value })}
                  >
                    <option value="">Select when this email is sent...</option>
                    {STATUS_TRIGGERS.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    This determines when the email notification is sent to customers
                  </p>
                </div>

                {/* Template Name */}
                <div>
                  <label className="label">Template Name *</label>
                  <input
                    type="text"
                    className="input"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    placeholder="e.g., Custom Notification"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="label">Description</label>
                  <input
                    type="text"
                    className="input"
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    placeholder="Brief description of when this template is used"
                  />
                </div>

                {/* Submission Type */}
                <div>
                  <label className="label">Applies To</label>
                  <select
                    className="input"
                    value={newTemplate.submission_type}
                    onChange={(e) => setNewTemplate({ ...newTemplate, submission_type: e.target.value })}
                  >
                    <option value="">All Submission Types</option>
                    <option value="text_giving">Text Giving Only</option>
                    <option value="messaging">Messaging Only</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    If set, this template will only be used for the selected submission type.
                  </p>
                </div>

                {/* Subject */}
                <div>
                  <label className="label">Email Subject *</label>
                  <input
                    type="text"
                    className="input"
                    value={newTemplate.subject}
                    onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                    placeholder="e.g., Your verification request update"
                  />
                </div>

                {/* Available Variables */}
                <div>
                  <label className="label">Available Variables</label>
                  <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded border border-blue-200">
                    {DEFAULT_AVAILABLE_VARIABLES.map((variable, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 text-sm font-mono bg-blue-100 text-blue-700 rounded"
                      >
                        {`{{${variable}}}`}
                      </span>
                    ))}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    These variables can be used in your template and will be replaced with actual values
                  </p>
                </div>

                {/* HTML Body */}
                <div>
                  <label className="label">Email Body (HTML) *</label>
                  <textarea
                    className="input font-mono text-sm"
                    rows={15}
                    value={newTemplate.html_body}
                    onChange={(e) => setNewTemplate({ ...newTemplate, html_body: e.target.value })}
                    placeholder="<p>Hello {{contact_name}},</p>&#10;&#10;<p>Your verification request is being processed...</p>"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Use HTML tags for formatting. Variables like {'{{contact_name}}'} will be replaced with actual values.
                  </p>
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={newTemplate.is_active}
                    onChange={(e) => setNewTemplate({ ...newTemplate, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">
                    Active (template will be used for sending emails)
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={handleCreateTemplate}
                    disabled={saving}
                    className="btn btn-primary whitespace-nowrap"
                  >
                    {saving ? 'Creating...' : 'Create Template'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates List */}
        {!editingTemplate && !isCreating && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <div key={template.id} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                        Triggers: {getStatusLabel(template.template_key)}
                      </span>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                        template.submission_type === 'messaging'
                          ? 'bg-blue-100 text-blue-800'
                          : template.submission_type === 'text_giving'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {template.submission_type === 'messaging' ? 'Messaging' :
                         template.submission_type === 'text_giving' ? 'Text Giving' : 'All Types'}
                      </span>
                    </div>
                  </div>
                  {template.is_active ? (
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                      Active
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                      Inactive
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-600 mb-4">{template.description}</p>

                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Subject:</p>
                  <p className="text-sm text-gray-900">{template.subject}</p>
                </div>

                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Available Variables:</p>
                  <div className="flex flex-wrap gap-1">
                    {template.available_variables.map((variable, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 rounded"
                      >
                        {`{{${variable}}}`}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-gray-500 mb-4">
                  Last updated: {new Date(template.updated_at).toLocaleString()}
                  {template.updated_by && ` by ${template.updated_by}`}
                </div>

                <button
                  onClick={() => handleEdit(template)}
                  className="w-full btn btn-secondary text-sm"
                >
                  {isAdmin ? 'Edit Template' : 'View Template'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Template Editor */}
        {editingTemplate && (
          <div className="space-y-6">
            <div className="card">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  {isAdmin ? 'Edit' : 'View'} Template: {editingTemplate.name}
                </h2>
                <button
                  onClick={handleCancel}
                  className="btn btn-secondary whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>

              <div className="space-y-4">
                {/* Triggered By Status (read-only) */}
                <div>
                  <label className="label">Triggered By Status</label>
                  <div className="input bg-gray-100 text-gray-700 cursor-not-allowed">
                    {getStatusLabel(editingTemplate.template_key)}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    This template is sent when a submission reaches this status. To change the trigger, create a new template.
                  </p>
                </div>

                {/* Template Name */}
                <div>
                  <label className="label">Template Name</label>
                  <input
                    type="text"
                    className="input"
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="label">Description</label>
                  <input
                    type="text"
                    className="input"
                    value={editingTemplate.description || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>

                {/* Submission Type */}
                <div>
                  <label className="label">Applies To</label>
                  <select
                    className="input"
                    value={editingTemplate.submission_type || ''}
                    onChange={(e) => setEditingTemplate({
                      ...editingTemplate,
                      submission_type: e.target.value || null
                    })}
                    disabled={!isAdmin}
                  >
                    <option value="">All Submission Types</option>
                    <option value="text_giving">Text Giving Only</option>
                    <option value="messaging">Messaging Only</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    If set, this template will only be used for the selected submission type.
                    Type-specific templates override general templates.
                  </p>
                </div>

                {/* Subject */}
                <div>
                  <label className="label">Email Subject</label>
                  <input
                    type="text"
                    className="input"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>

                {/* Available Variables */}
                <div>
                  <label className="label">Available Variables</label>
                  <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded border border-blue-200">
                    {editingTemplate.available_variables.map((variable, idx) => (
                      <button
                        key={idx}
                        onClick={() => isAdmin && insertVariable(variable)}
                        className={`px-3 py-1.5 text-sm font-mono bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors ${!isAdmin && 'cursor-not-allowed opacity-60'}`}
                        title={isAdmin ? 'Click to insert into template' : 'View only'}
                      >
                        {`{{${variable}}}`}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    {isAdmin ? 'Click a variable to insert it at cursor position' : 'These variables will be replaced with actual values'}
                  </p>
                </div>

                {/* HTML Body */}
                <div>
                  <label className="label">Email Body (HTML)</label>
                  <textarea
                    id="html-body-textarea"
                    className="input font-mono text-sm"
                    rows={20}
                    value={editingTemplate.html_body}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, html_body: e.target.value })}
                    disabled={!isAdmin}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Use HTML tags for formatting. Variables like {'{{contact_name}}'} will be replaced with actual values.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={handlePreview}
                    className="btn btn-secondary whitespace-nowrap"
                  >
                    Preview with Sample Data
                  </button>
                  {isAdmin && (
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="btn btn-primary whitespace-nowrap"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Preview Section */}
            {previewHtml && (
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Subject:</p>
                    <div className="p-3 bg-gray-50 rounded border border-gray-200">
                      {previewHtml.subject}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Email Body:</p>
                    <div className="p-4 bg-white rounded border border-gray-200">
                      <div
                        dangerouslySetInnerHTML={{ __html: previewHtml.html_body }}
                        className="prose prose-sm max-w-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
